import { useCallback } from "react";
import { checkUpdateWorkOrder, updateWorkOrder } from "../../api/WorkOrders";
import { logCustomMessage } from "../../util/logger";
import { SeverityLevel } from "@microsoft/applicationinsights-web";

export interface UpdateWoResult {
  success: boolean;
  message?: string;
  response?: any;
}

/**
 * Saves an existing work order via WorkOrders/UpdateWorkOrder.
 * Runs WorkOrders/CheckUpdateWorkOrder first as a validation gate (mirrors
 * the pattern used in EquipmentResults) and surfaces the server message on
 * failure. The caller is responsible for building the { RequestObject } payload.
 */
export default function useUpdateWo() {
  const updateWo = useCallback(
    async (payload: any): Promise<UpdateWoResult> => {
      const checkResponse = await checkUpdateWorkOrder(payload);
      logCustomMessage(
        "Check Update Work Order from WO detail custom fields",
        SeverityLevel.Information,
        "WorkOrders/CheckUpdateWorkOrder",
        payload,
        checkResponse
      );

      if (checkResponse.isError || checkResponse.data?.Success !== true) {
        return {
          success: false,
          message: checkResponse.data?.MessageList?.[0]?.Text,
          response: checkResponse,
        };
      }

      const updateResponse = await updateWorkOrder(payload);
      logCustomMessage(
        "Update Work Order from WO detail custom fields",
        SeverityLevel.Information,
        "WorkOrders/UpdateWorkOrder",
        payload,
        updateResponse
      );

      if (updateResponse.isError || updateResponse.data?.Success !== true) {
        return {
          success: false,
          message: updateResponse.data?.MessageList?.[0]?.Text,
          response: updateResponse,
        };
      }

      return { success: true, response: updateResponse };
    },
    []
  );

  return { updateWo };
}
